<template>
  <div class="w-full flex items-center justify-center mb-4 md:mb-0">
    <!-- right side -->
    <div
      class="flex flex-col bg-white w-full md:w-[75%] lg:w-[55%] xl:w-[45%] 2xl:w-[35%] px-6 py-2 items-center space-y-1 border border-slate-200 shadow-lg rounded-lg h-auto"
    >
      <p class="text-subDark font-bold text-base md:text-xl">
        {{ $t("Create_Your_Account_and_Start_Your_Journey") }}
      </p>
      <p class="text-slate-500 font-medium text-xs text-center">
        {{ $t("Fill_in_your_details_below_to_j") }}
      </p>
      <div
        :class="{
          'md:space-x-2': locale != 'ar',
          'md:space-x-reverse-2': locale === 'ar',
        }"
        class="flex flex-col md:flex-row w-full"
      >
        <div
          :class="{ 'pl-2': locale === 'ar' }"
          class="relative w-full flex flex-col space-y-1"
        >
          <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
            {{ $t("First_name") }}
          </p>
          <input
            v-model="fname"
            type="string"
            :maxlength="PROFILE_NAME_MAX_LENGTH"
            class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
            :placeholder="$t('First_name')"
            required="true"
          />
        </div>
        <div class="relative w-full flex flex-col space-y-1">
          <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
            {{ $t("Last_name") }}
          </p>
          <input
            v-model="lname"
            type="string"
            :maxlength="PROFILE_NAME_MAX_LENGTH"
            class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
            :placeholder="$t('Last_name')"
            required="true"
          />
        </div>
      </div>
      <div
        :class="{
          'md:space-x-2': locale != 'ar',
          'md:space-x-reverse-2': locale === 'ar',
        }"
        class="flex flex-col md:flex-row w-full"
      >
        <div
          :class="{ 'pl-2': locale === 'ar' }"
          class="relative w-full flex flex-col space-y-1"
        >
          <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
            {{ $t("Email") }}
          </p>
          <input
            v-model="email"
            type="string"
            class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
            :placeholder="$t('Email')"
            required="true"
          />
        </div>
        <div class="relative w-full flex flex-col space-y-1">
          <p class="text-slate-900 font-semibold text-sm pt-2 pb-1">
            {{ $t("Password") }}
          </p>
          <div class="relative w-full">
            <input
              v-model="password"
              :type="viewPassword ? 'text' : 'password'"
              class="bg-white pl-3 border focus:border-transparent border-gray-300 outline-none text-gray-900 rounded-lg w-full p-4 h-10"
              :placeholder="$t('Password')"
              required="true"
            />
            <button
              type="button"
              :class="{ 'right-2': locale != 'ar', 'left-2': locale === 'ar' }"
              class="absolute inset-y-0 flex items-center pr-3 text-gray-600"
              @click="togglePassword"
            >
              <svg
                v-if="viewPassword"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  d="m17.069 6.546 2.684-2.359c.143-.125.32-.187.497-.187.418 0 .75.34.75.75 0 .207-.086.414-.254.562l-16.5 14.501c-.142.126-.319.187-.496.187-.415 0-.75-.334-.75-.75 0-.207.086-.414.253-.562l2.438-2.143c-1.414-1.132-2.627-2.552-3.547-4.028-.096-.159-.144-.338-.144-.517s.049-.358.145-.517c2.111-3.39 5.775-6.483 9.853-6.483 1.815 0 3.536.593 5.071 1.546zm2.319 1.83c.966.943 1.803 2.014 2.474 3.117.092.156.138.332.138.507s-.046.351-.138.507c-2.068 3.403-5.721 6.493-9.864 6.493-1.297 0-2.553-.313-3.729-.849l1.247-1.096c.795.285 1.626.445 2.482.445 3.516 0 6.576-2.622 8.413-5.5-.595-.932-1.318-1.838-2.145-2.637zm-3.434 3.019c.03.197.046.399.046.605 0 2.208-1.792 4-4 4-.384 0-.756-.054-1.107-.156l1.58-1.389c.895-.171 1.621-.821 1.901-1.671zm-.058-3.818c-1.197-.67-2.512-1.077-3.898-1.077-3.465 0-6.533 2.632-8.404 5.5.853 1.308 1.955 2.567 3.231 3.549l1.728-1.519c-.351-.595-.553-1.289-.553-2.03 0-2.208 1.792-4 4-4 .925 0 1.777.315 2.455.843zm-2.6 2.285c-.378-.23-.822-.362-1.296-.362-1.38 0-2.5 1.12-2.5 2.5 0 .36.076.701.213 1.011z"
                  fill-rule="nonzero"
                />
              </svg>
              <svg
                v-else
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  d="m11.998 5c-4.078 0-7.742 3.093-9.853 6.483-.096.159-.145.338-.145.517s.048.358.144.517c2.112 3.39 5.776 6.483 9.854 6.483 4.143 0 7.796-3.09 9.864-6.493.092-.156.138-.332.138-.507s-.046-.351-.138-.507c-2.068-3.403-5.721-6.493-9.864-6.493zm8.413 7c-1.837 2.878-4.897 5.5-8.413 5.5-3.465 0-6.532-2.632-8.404-5.5 1.871-2.868 4.939-5.5 8.404-5.5 3.518 0 6.579 2.624 8.413 5.5zm-8.411-4c2.208 0 4 1.792 4 4s-1.792 4-4 4-4-1.792-4-4 1.792-4 4-4zm0 1.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"
                  fill-rule="nonzero"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <p v-if="hasError" class="text-lg font-semibold text-red-600">
        {{ errorMessage }}
      </p>
      <div class="pt-2"></div>
      <button
        @click="signup"
        class="bg-subDark text-xs md:text-base text-white font-semibold py-1 md:px-12 rounded-lg w-full h-10"
      >
        {{ $t("Sign_Up_and_Start_Creating") }}
      </button>
      <div class="flex flex-row w-full">
        <div class="border-b-[1px] border-slate-300 w-full mb-4"></div>
        <p class="text-sm text-center text-slate-500 font-medium py-2 px-2">
          {{ $t("OR") }}
        </p>
        <div class="border-b-[1px] border-slate-300 w-full mb-4"></div>
      </div>
      <button
        @click="googleSignup"
        class="bg-white border border-slate-300 w-full rounded-md text-[#061B44] font-semibold flex flex-row items-center justify-center h-10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="35"
          height="25"
          viewBox="1325.916 305 37.551 38.316"
        >
          <g data-name="Groupe 5578">
            <g data-name="Google__G__logo">
              <path
                d="M1363.466 324.594c0-1.359-.122-2.665-.348-3.92h-18.044v7.42h10.31c-.452 2.386-1.81 4.407-3.848 5.765v4.825h6.217c3.623-3.344 5.713-8.256 5.713-14.09Z"
                fill="#4285f4"
                fill-rule="evenodd"
                data-name="Tracé 9724"
              />
              <path
                d="M1345.074 343.316c5.173 0 9.51-1.706 12.68-4.632l-6.218-4.825c-1.707 1.15-3.884 1.846-6.462 1.846-4.98 0-9.213-3.361-10.728-7.89h-6.375v4.947c3.153 6.253 9.614 10.554 17.103 10.554Z"
                fill="#34a853"
                fill-rule="evenodd"
                data-name="Tracé 9725"
              />
              <path
                d="M1334.346 327.798a11.48 11.48 0 0 1-.61-3.64c0-1.271.226-2.49.61-3.64v-4.946h-6.375a18.922 18.922 0 0 0-2.055 8.586c0 3.1.749 6.009 2.055 8.587l4.964-3.867 1.41-1.08Z"
                fill="#fbbc05"
                fill-rule="evenodd"
                data-name="Tracé 9726"
              />
              <path
                d="M1345.074 312.628c2.822 0 5.33.976 7.333 2.857l5.486-5.486c-3.327-3.1-7.646-4.999-12.819-4.999-7.489 0-13.95 4.302-17.103 10.572l6.375 4.946c1.515-4.528 5.747-7.89 10.728-7.89Z"
                fill="#ea4335"
                fill-rule="evenodd"
                data-name="Tracé 9727"
              />
            </g>
          </g>
        </svg>
        <p class="text-slate-700 text-xs md:text-base font-medium px-2">
          {{ $t("Sign_up_with_google") }}
        </p>
      </button>
      <div class="flex flex-col">
        <p class="text-xs pt-2 text-center text-slate-500 font-medium">
          {{ $t("By_Proceeding_you_agree") }}
        </p>
        <div
          class="text-xs underline underline-offset-4 pb-2 text-center text-slate-700 font-medium mt-2 flex flex-row"
        >
          <p>{{ $t("toour") }}</p>
          &nbsp;
          <a href="/terms">{{ $t("Termsofuse") }}</a>
          &nbsp;
          <p>{{ $t("and") }}</p>
          &nbsp;
          <a href="/privacy-policy"> {{ $t("privacy-policy") }}</a>
        </div>
      </div>
      <p class="text-sm md:text-base font-medium text-slate-600">
        {{ $t("Already_have_an_account") }}
        <a class="text-[#4867C8]" href="/signing">{{ $t("Log_in") }}</a>
      </p>
    </div>
    <!-- left side -->
    <!-- <div
      class="flex flex-col bg-white lg:w-[50%] items-center justify-center lg:py-0"
    >
      <img
        src="../assets/images/auth/01.png"
        alt=""
        class="w-[65%] lg:w-[40%]"
      />
    </div> -->
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "../store/auth";
import {
  API_BASE_URL,
  GETCLIENTCOUNTRY,
  GOOGLEAUTH,
  SIGNUP,
} from "../utils/appConst";
import { PROFILE_NAME_MAX_LENGTH } from "../utils/profileNameLimits";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useI18n } from "vue-i18n";
import {
  decodeCredential,
  googleAuthCodeLogin,
  googleTokenLogin,
} from "vue3-google-login";
import { useUserProfile } from "../store/userProfile";

const router = useRouter();
const email = ref("");
const password = ref("");
const fname = ref("");
const lname = ref("");
let userToken = "";
const passwordNotMach = ref(false);
const signupFailed = ref(false);
const viewPassword = ref(false);
const alreadyExist = ref(false);
const hasError = ref(false);
const errorMessage = ref("");
const authStore = useAuthStore();
const userStore = useUserProfile();
const signupUrl = `${API_BASE_URL}${SIGNUP}`;
const getClientCountryUrl = `${API_BASE_URL}${GETCLIENTCOUNTRY}`;
const googleAuthUrl = `${API_BASE_URL}${GOOGLEAUTH}`;
const { t, locale } = useI18n();
let clientCountry = "";
let isLoading = false;

onMounted(async () => {
  await fetchClientCountry();
});

const togglePassword = () => {
  viewPassword.value = !viewPassword.value;
};

const fetchClientCountry = async () => {
  try {
    /* const response = await fetch(getClientCountryUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "69420",
      },
    });
    const result = await response.json();
    if (!result.message) {
      return;
    }
    clientCountry = result.country; */
    const res = await fetch("https://www.cloudflare.com/cdn-cgi/trace");
    const text = await res.text();
    const match = text.match(/loc=(\w+)/);
    clientCountry = match ? match[1] : "US";
  } catch (error) {
    console.error("Authentication failed:", error);
  }
};

const resetStatusValues = () => {
  passwordNotMach.value = false;
  signupFailed.value = false;
  alreadyExist.value = false;
  hasError.value = false;
};

const signup = async () => {
  if (isLoading) {
    return;
  }
  resetStatusValues();
  if (fname.value.trim().length == 0 || lname.value.trim().length == 0) {
    /* errorMessage.value = t(
      "first-and-last-names-are-required-fields-please-fill-them-in-thanks"
    );
    hasError.value = true; */
    toast(
      t("first-and-last-names-are-required-fields-please-fill-them-in-thanks"),
      {
        autoClose: 2500,
        type: "error",
        theme: "light",
      }
    );
    return;
  }
  if (
    fname.value.trim().length > PROFILE_NAME_MAX_LENGTH ||
    lname.value.trim().length > PROFILE_NAME_MAX_LENGTH
  ) {
    toast(
      t("profile-name-exceeds-max-length", {
        max: PROFILE_NAME_MAX_LENGTH,
      }),
      {
        autoClose: 3000,
        type: "error",
        theme: "light",
      }
    );
    return;
  }
  if (!isValidEmail(email.value)) {
    /* errorMessage.value = t("oops-invalid-email-please-check-and-retry-thanks");
    hasError.value = true; */
    toast(t("oops-invalid-email-please-check-and-retry-thanks"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
    return;
  }
  if (password.value.length < 8) {
    /* errorMessage.value = t(
      "password-too-short-minimum-8-characters-required-please-try-again-thanks"
    );
    hasError.value = true; */
    toast(
      t(
        "password-too-short-minimum-8-characters-required-please-try-again-thanks"
      ),
      {
        autoClose: 2500,
        type: "error",
        theme: "light",
      }
    );
    return;
  }

  isLoading = true;
  let resolveSignup: (value?: unknown) => void;
  let rejectSignup: (reason?: any) => void;
  try {
    const signupPromise = new Promise(async (resolve, reject) => {
      resolveSignup = resolve;
      rejectSignup = reject;
      const response = await fetch(signupUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          email: email.value.trim(),
          password: password.value,
          fname: fname.value.trim(),
          lname: lname.value.trim(),
          countryCode: clientCountry,
          fcmToken: authStore.fcmToken,
        }),
      });

      const result = await response.json();

      if (result.error === "PROFILE_NAME_TOO_LONG") {
        signupFailed.value = true;
        rejectSignup(
          t("profile-name-exceeds-max-length", {
            max: result.maxLength ?? PROFILE_NAME_MAX_LENGTH,
          })
        );
        return;
      }

      if (result.error && result.error == "This user already exists!") {
        alreadyExist.value = true;
        rejectSignup(
          t(
            "this-email-already-exists-please-try-logging-in-or-use-a-different-email-thanks"
          )
        );
        return;
      }

      if (!result.token) {
        signupFailed.value = true;
        rejectSignup();
        return;
      }
      userToken = result.token;
      resolveSignup();
    });

    toast.promise(
      signupPromise,
      {
        pending: t("signing-up"),
        success: t("sign-up-successful"),
      },
      {
        position: toast.POSITION.TOP_CENTER,
        autoClose: true,
        closeOnClick: true,
        theme: "light",
      }
    );
    signupPromise
      .then(async () => {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        authStore.setToken(userToken);
        authStore.setUsingGoogleAuth(false);
        await authStore.getUnseenCount();
        await userStore.getUserSubscription(authStore.token!);
        router.push("/pricing");
      })
      .catch((error: any) => {
        toast(error || t("sign-up-failed"), {
          autoClose: 2500,
          type: "error",
          position: toast.POSITION.TOP_CENTER,
          theme: "light",
        });
        console.error("Authentication failed:", error);
        isLoading = false;
      });
  } catch (error: any) {
    console.error("Authentication failed:", error);
    toast(error || t("sign-up-failed"), {
      autoClose: 2500,
      type: "error",
      position: toast.POSITION.TOP_CENTER,
      theme: "light",
    });
    isLoading = false;
  }
};

function isValidEmail(email: string) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

const googleSignup = () => {
  googleTokenLogin().then(async (response) => {
    if (response.access_token) {
      toast(t("signing-up"), {
        autoClose: 2500,
        type: "info",
        theme: "light",
      });
      const reqResponse = await fetch(googleAuthUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "69420",
        },
        body: JSON.stringify({
          credential: response.access_token,
          userFcmToken: authStore.fcmToken,
          countryCode: clientCountry,
        }),
      });

      const result = await reqResponse.json();

      if (result.error) {
        alreadyExist.value = true;
        toast(
          t(
            "this-email-already-exists-please-try-logging-in-or-use-a-different-email-thanks"
          ),
          {
            autoClose: 2500,
            type: "error",
            theme: "light",
          }
        );
        return;
      }
      if (result.token) {
        toast(t("sign-up-successful"), {
          autoClose: 2500,
          type: "success",
          theme: "light",
        });
        userToken = result.token;
        authStore.setToken(userToken);
        authStore.setUsingGoogleAuth(true);
        await authStore.getUnseenCount();
        await userStore.getUserSubscription(authStore.token!);
        router.push("/pricing");
      }
    }
  });
};

const navigate = (path: string) => {
  router.push(`/${path}`);
};
</script>
